import { Typography } from "@mui/material";
import {
  ASTableV2,
  ASTableV2Body,
  ASTableV2Cell,
  ASTableV2Header,
  ASTableV2Row
} from "@qubit/autoparts";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch } from "~/app/store";

import { TemperatureZoneBadgeCell } from "~/components/TemperatureZoneBadgeCell";
import { useGetMultiTempGridInfo } from "~/hooks/useGetMultiTempGridInfo";
import {
  formatCycleCountProductMixpanelRowForVariantInList,
  formatCycleCountProductMixpanelRowsForTableSelection,
  mixpanelTrack,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";
import { IncompleteCycleCountResponseDto } from "~/types/api";

import { CycleCountsSearch } from "./CycleCountsSearch";
import { CycleCountsTableProps } from "./CycleCountsTableWrapper";
import { setPage } from "./cycleCountsNew.slice";

const CycleCountsTableV2 = ({
  cycleCountData,
  cycleCountVariantData,
  selectedRows,
  isLoadingData,
  setSelectedRows,
  totalPageCount,
  page
}: CycleCountsTableProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const trackedPageName = useMixpanelPageName();
  const { isMultiTempGrid } = useGetMultiTempGridInfo();

  const handleRowClick = useCallback(
    (row: IncompleteCycleCountResponseDto) => {
      const isAlreadySelected = selectedRows.some(
        (selectedRow) => selectedRow.cycleCountId === row.cycleCountId
      );

      if (isAlreadySelected) {
        const remainingCount = selectedRows.filter(
          (selectedRow) => selectedRow.cycleCountId !== row.cycleCountId
        ).length;
        mixpanelTrack({
          trackedPageName,
          type: "Unselect Row",
          label: "Deselect Product",
          eventProperties: {
            "Deselected Cycle Count Product Data":
              formatCycleCountProductMixpanelRowForVariantInList(
                row.variantId,
                cycleCountVariantData ?? []
              ),
            "Remaining selected product count": remainingCount
          }
        });
        setSelectedRows(
          selectedRows.filter(
            (selectedRow) => selectedRow.cycleCountId !== row.cycleCountId
          )
        );
      } else {
        mixpanelTrack({
          trackedPageName,
          type: "Select Row",
          label: "Select Product",
          eventProperties: {
            "List of selected Cycle Counts Product Data":
              formatCycleCountProductMixpanelRowsForTableSelection(
                [...selectedRows, { variantId: row.variantId }],
                cycleCountVariantData ?? []
              )
          }
        });
        setSelectedRows([
          ...selectedRows,
          {
            cycleCountId: row.cycleCountId,
            variantId: row.variantId,
            binCount: row.countingTasks.length
          }
        ]);
      }
    },
    [cycleCountVariantData, selectedRows, setSelectedRows, trackedPageName]
  );

  const getProductByVariantId = useCallback(
    (variantId: string) => {
      return cycleCountVariantData?.find(
        (variant) => variant.variantId === variantId
      );
    },
    [cycleCountVariantData]
  );

  const selectedCycleCountIds = useMemo(
    () => selectedRows.map((row) => row.cycleCountId),
    [selectedRows]
  );

  const contentColumnsLayout = "40% 30% 30%";
  const gridTemplateColumns = isMultiTempGrid
    ? `28px calc(40% - 7px) calc(30% - 7px) calc(30% - 14px)`
    : contentColumnsLayout;

  return (
    <ASTableV2
      isLoading={isLoadingData}
      gridTemplateColumns={gridTemplateColumns}
      gridTemplateRows={`50px repeat(${cycleCountData.cycleCounts.length}, 60px)`}
      pagination={{
        setPage: (newPage: number) => {
          dispatch(setPage(newPage));
          if (window.scrollTo) window.scrollTo(0, 0);
        },
        page: page,
        pageCount: totalPageCount
      }}
      searchComponent={<CycleCountsSearch />}
    >
      <ASTableV2Header>
        <ASTableV2Row role="presentation">
          {isMultiTempGrid && <ASTableV2Cell />}
          <ASTableV2Cell>
            <Typography variant="tableBody">{t("product")}</Typography>
          </ASTableV2Cell>
          <ASTableV2Cell>
            <Typography variant="tableBody">{t("bin count")}</Typography>
          </ASTableV2Cell>
          <ASTableV2Cell>
            <Typography variant="tableBody">{t("due by")}</Typography>
          </ASTableV2Cell>
        </ASTableV2Row>
      </ASTableV2Header>
      <ASTableV2Body>
        {cycleCountData.cycleCounts.map(
          (row: IncompleteCycleCountResponseDto) => {
            const product = getProductByVariantId(row.variantId);
            const isSelected = selectedCycleCountIds.includes(row.cycleCountId);

            return (
              <ASTableV2Row
                key={row.cycleCountId}
                onClick={() => handleRowClick(row)}
                selected={isSelected}
              >
                <TemperatureZoneBadgeCell tempZone={product?.temperatureZone} />
                <ASTableV2Cell>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: {
                        xs: "120px",
                        sm: "200px",
                        md: "300px",
                        lg: "475px"
                      }
                    }}
                  >
                    {product?.productName}
                  </Typography>
                </ASTableV2Cell>
                <ASTableV2Cell>
                  <Typography variant="body2">
                    {row.countingTasks.length}
                  </Typography>
                </ASTableV2Cell>
                <ASTableV2Cell>
                  <Typography variant="body2">{row.dueDate}</Typography>
                </ASTableV2Cell>
              </ASTableV2Row>
            );
          }
        )}
      </ASTableV2Body>
    </ASTableV2>
  );
};

export default CycleCountsTableV2;
